import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useRequestPasswordReset } from '@/api/auth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { apiError } from '@/lib/api';
import { BillerBayLogo } from '@/components/ui/BillerBayLogo';

export function PasswordResetRequestPage() {
  usePageTitle('Reset password');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const req = useRequestPasswordReset();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await req.mutateAsync({ email });
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bb-bg p-4">
      <div className="w-full max-w-md bg-white  shadow-sm border border-bb-lilac/40 p-8">
        <BillerBayLogo variant="dark" height={28} className="mb-5" />
        <h1 className="text-xl font-bold text-bb-navy mb-1">Reset password</h1>
        <p className="text-sm text-slate-500 mb-6">
          Enter your email and we'll send a reset link.
        </p>

        {sent ? (
          <div className="text-sm text-green-800 bg-green-50 border border-green-100 px-3 py-3">
            If that email exists in our system, a reset link has been sent.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2">
                {error}
              </div>
            )}
            <Button type="submit" variant="primary" loading={req.isPending} disabled={req.isPending} className="w-full justify-center">
              {req.isPending ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>
        )}

        <div className="mt-4 text-sm">
          <Link to="/login" className="text-bb-navy hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
